import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const AdminManagement = () => {
  const { setPageTitle } = usePageContext();
  const [admins, setAdmins] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    permissions: []
  });
  const { hasPermission } = useAuth();

  const modules = [
    { name: 'admin', actions: ['create', 'read', 'update', 'delete'] },
    { name: 'employee', actions: ['create', 'read', 'update', 'delete'] },
    { name: 'client', actions: ['create', 'read', 'update', 'delete'] },
    { name: 'vendor', actions: ['create', 'read', 'update', 'delete'] },
    { name: 'recruiter', actions: ['create', 'read', 'update', 'delete'] },
    { name: 'condition', actions: ['create', 'read', 'update', 'delete'] },
    { name: 'invoice', actions: ['create', 'read', 'update', 'delete'] },
    { name: 'expense', actions: ['create', 'read', 'update', 'delete', 'approve'] }
  ];

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin');
      setAdmins(response.data);
    } catch (error) {
      toast.error('Error fetching admins');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageTitle('Admin Management');
    if (hasPermission('admin', 'read')) {
      fetchAdmins();
    }
  }, [setPageTitle, hasPermission]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      if (editingAdmin) {
        await axios.put(`/api/admin/${editingAdmin._id}`, formData);
        toast.success('Admin updated successfully');
      } else {
        await axios.post('/api/admin', formData);
        toast.success('Admin created successfully');
      }
      setShowModal(false);
      setEditingAdmin(null);
      setFormData({ name: '', email: '', password: '', permissions: [] });
      setSelectedAdmins([]);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedAdmins.map(id => axios.delete(`/api/admin/${id}`)));
      toast.success(`${selectedAdmins.length} admins deleted successfully`);
      setSelectedAdmins([]);
      setShowDeleteModal(false);
      fetchAdmins();
    } catch (error) {
      toast.error('Error deleting admins');
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      permissions: admin.permissions
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await axios.delete(`/api/admin/${id}`);
        toast.success('Admin deleted successfully');
        fetchAdmins();
      } catch (error) {
        toast.error('Error deleting admin');
      }
    }
  };

  const handlePermissionChange = (module, action, checked) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];
      const moduleIndex = permissions.findIndex(p => p.module === module);
      
      if (moduleIndex === -1) {
        if (checked) {
          permissions.push({ module, actions: [action] });
        }
      } else {
        const actions = [...permissions[moduleIndex].actions];
        if (checked) {
          if (!actions.includes(action)) {
            actions.push(action);
          }
        } else {
          const actionIndex = actions.indexOf(action);
          if (actionIndex > -1) {
            actions.splice(actionIndex, 1);
          }
        }
        
        if (actions.length === 0) {
          permissions.splice(moduleIndex, 1);
        } else {
          permissions[moduleIndex] = { module, actions };
        }
      }
      
      return { ...prev, permissions };
    });
  };

  if (!hasPermission('admin', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="text-muted mb-0">Manage system administrators and their permissions</p>
          {selectedAdmins.length > 0 && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <span className="badge bg-primary">{selectedAdmins.length} selected</span>
              <button className="btn btn-sm btn-danger" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash me-1"></i>Delete Selected
              </button>
            </div>
          )}
        </div>
        {hasPermission('admin', 'create') && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Add Admin
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading administrators...</p>
        </div>
      ) : admins.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '4rem', opacity: 0.3 }}>👥</div>
          <h5 className="text-muted mt-3">No administrators found</h5>
          <p className="text-muted">Add administrators to manage system access and permissions.</p>
          {hasPermission('admin', 'create') && (
            <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add First Admin
            </button>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAdmins(admins.map(admin => admin._id));
                      } else {
                        setSelectedAdmins([]);
                      }
                    }}
                    checked={selectedAdmins.length === admins.length && admins.length > 0}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map(admin => (
                <tr key={admin._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedAdmins.includes(admin._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAdmins([...selectedAdmins, admin._id]);
                        } else {
                          setSelectedAdmins(selectedAdmins.filter(id => id !== admin._id));
                        }
                      }}
                    />
                  </td>
                  <td>{admin.name}</td>
                  <td>{admin.email}</td>
                  <td>
                    <span className={`badge ${admin.isActive ? 'bg-success' : 'bg-danger'}`}>
                      {admin.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {hasPermission('admin', 'update') && (
                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(admin)}>
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                      {hasPermission('admin', 'delete') && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(admin._id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingAdmin ? 'Edit Admin' : 'Add Admin'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  {!editingAdmin && (
                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Permissions</label>
                    {modules.map(module => (
                      <div key={module.name} className="card mb-2">
                        <div className="card-header">
                          <strong>{module.name.charAt(0).toUpperCase() + module.name.slice(1)}</strong>
                        </div>
                        <div className="card-body">
                          {module.actions.map(action => (
                            <div key={action} className="form-check form-check-inline">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`${module.name}-${action}`}
                                checked={formData.permissions.some(p => p.module === module.name && p.actions.includes(action))}
                                onChange={(e) => handlePermissionChange(module.name, action, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`${module.name}-${action}`}>
                                {action}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {editingAdmin ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingAdmin ? 'Update' : 'Create'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-warning" style={{fontSize: '3rem'}}></i>
                  <h5 className="mt-3">Are you sure?</h5>
                  <p className="text-muted">
                    You are about to delete {selectedAdmins.length} administrator(s). This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleBulkDelete}>
                  <i className="fas fa-trash me-2"></i>Delete {selectedAdmins.length} Admin(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;